import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

interface CalendlyTokenRequest {
  grantType: string;
  code?: string;
  refreshToken?: string;
}

interface CalendlyTokenResponse {
  tokenType: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  scope?: string;
}

interface CalendlyUserResponse {
  resource: {
    uri: string;
    name: string;
    slug: string;
    email: string;
    scheduling_url: string;
    timezone: string;
    avatar_url: string;
    created_at: string;
    updated_at: string;
  };
}

interface CalendlyScheduledEventsResponse {
  collection: any[];
  pagination: {
    count: number;
    next_page?: string;
    previous_page?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CalendlyService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Exchange authorization code for access token
   */
  exchangeAuthorizationCode(code: string): Observable<CalendlyTokenResponse> {
    const payload: CalendlyTokenRequest = {
      grantType: 'authorization_code',
      code: code
    };

    return this.http.post<CalendlyTokenResponse>(
      `${this.API_URL}Calendly/exchange-token`,
      payload
    );
  }

  /**
   * Refresh access token using refresh token
   */
  refreshAccessToken(refreshToken: string): Observable<CalendlyTokenResponse> {
    const payload: CalendlyTokenRequest = {
      grantType: 'refresh_token',
      refreshToken: refreshToken
    };

    return this.http.post<CalendlyTokenResponse>(
      `${this.API_URL}Calendly/exchange-token`,
      payload
    );
  }

  /**
   * Get Calendly user information
   */
  getCalendlyUser(accessToken: string): Observable<CalendlyUserResponse> {
    const headers = new HttpHeaders({
      'X-Calendly-Token': accessToken
    });

    return this.http.get<CalendlyUserResponse>(
      `${this.API_URL}Calendly/user`,
      { headers }
    );
  }

  /**
   * Get scheduled events for a user
   */
  getScheduledEvents(accessToken: string, userUri: string): Observable<CalendlyScheduledEventsResponse> {
    const headers = new HttpHeaders({
      'X-Calendly-Token': accessToken
    });

    return this.http.get<CalendlyScheduledEventsResponse>(
      `${this.API_URL}Calendly/scheduled-events?userUri=${encodeURIComponent(userUri)}`,
      { headers }
    );
  }

  /**
   * Initiate Calendly OAuth login
   */
  initiateOAuthLogin(): void {
    const authUrl = `https://auth.calendly.com/oauth/authorize?client_id=${environment.calendlyClientId}&response_type=code&redirect_uri=${environment.calendlyRedirectUri}`;
    window.open(authUrl, '_blank');
  }
}